import { X, FileText, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import type { AttachmentState } from "@/hooks/useBuilderAttachments";

interface AttachmentChipsProps {
  attachments: AttachmentState[];
  onRemove: (id: string) => void;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function StatusIcon({ status }: { status: AttachmentState['status'] }) {
  switch (status) {
    case 'uploading':
    case 'extracting':
      return <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />;
    case 'ready':
      return <CheckCircle2 className="h-3.5 w-3.5 text-success" />;
    case 'failed':
      return <AlertCircle className="h-3.5 w-3.5 text-destructive" />;
  }
}

function statusLabel(status: AttachmentState['status']): string {
  switch (status) {
    case 'uploading': return 'Uploading...';
    case 'extracting': return 'Extracting text...';
    case 'ready': return 'Ready';
    case 'failed': return 'Failed';
  }
}

export default function AttachmentChips({ attachments, onRemove }: AttachmentChipsProps) {
  if (attachments.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2 px-1 mb-2">
      {attachments.map((attachment) => (
        <div
          key={attachment.id}
          className={`
            inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs
            ${attachment.status === 'failed'
              ? 'border-destructive/30 bg-destructive/5 text-destructive'
              : 'border-border bg-muted/50 text-foreground'
            }
          `}
          title={attachment.status === 'failed' ? attachment.error : `${attachment.fileName} (${formatFileSize(attachment.fileSize)}) — ${statusLabel(attachment.status)}`}
        >
          <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="max-w-[120px] truncate font-medium">
            {attachment.fileName}
          </span>
          <StatusIcon status={attachment.status} />
          <button
            type="button"
            onClick={() => onRemove(attachment.id)}
            className="ml-0.5 rounded-sm p-0.5 hover:bg-foreground/10 transition-colors"
            aria-label={`Remove ${attachment.fileName}`}
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ))}
    </div>
  );
}
